/**
 * infra-graph 構成図の cytoscape 要素変換（俯瞰 / stack 詳細）。
 *
 * API レスポンス（@cortex/domain-admin-api の InfraGraph*）を cytoscape の
 * ElementDefinition[] に変換する純関数群。描画副作用を持たないため単体テストできる。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business infra-graph の俯瞰 / stack 詳細レスポンスを cytoscape ノード・エッジ定義へ変換する。
 * @graph-connects infra-graph-icons [calls] kind → 公式アイコン data URI 解決
 */
import type { ElementDefinition } from 'cytoscape';
import type {
  InfraGraphOverviewResponse,
  InfraGraphStackDetailResponse,
} from '../../../types/api.js';
import { iconForKind } from './infra-graph-icons.js';

/** 俯瞰 stack ノード id の接頭辞（リソース id との衝突回避）。 @graph-connects none */
export const STACK_NODE_PREFIX = 'S::';

/** エッジ種別 → 色。凡例・トグルと共有する。 @graph-connects none */
export const EDGE_COLOR: Record<string, string> = {
  uses_secret: '#f59e0b',
  iam_binding: '#ef4444',
  runs_as: '#3b82f6',
  references_stack: '#94a3b8',
  depends_on: '#64748b',
  prop_depends_on: '#475569',
};

/** エッジ種別の色を取得する（未知は灰）。 @graph-connects none */
function edgeColor(type: string): string {
  return EDGE_COLOR[type] ?? '#64748b';
}

/**
 * `cortex-prod-` プレフィックスを落とし、長すぎる名前を省略する。
 * @param name リソース名
 * @returns 表示用ラベル
 * @graph-connects none
 */
export function shortLabel(name: string): string {
  const stripped = name.replace(/^cortex-prod-/, '');
  return stripped.length > 40 ? `${stripped.slice(0, 38)}…` : stripped;
}

/**
 * 俯瞰 stack ノードのサイズ（リソース数に応じて 30〜76px）。
 * @param count stack 内リソース数
 * @param maxCount 全 stack 中の最大リソース数
 * @returns ノード直径(px)
 * @graph-connects none
 */
export function overviewNodeSize(count: number, maxCount: number): number {
  const ratio = maxCount > 0 ? Math.sqrt(count / maxCount) : 0;
  return 30 + Math.round(46 * ratio);
}

/**
 * 集約エッジの太さ（件数の対数スケール、最大 8）。
 * @param count 集約件数
 * @returns 線幅(px)
 * @graph-connects none
 */
export function aggregatedEdgeWidth(count: number): number {
  return Math.min(8, 1 + Math.log2(count + 1));
}

/**
 * 俯瞰ビューの cytoscape 要素（stack ノード + cross-stack 集約エッジ）を組み立てる。
 * @param overview 俯瞰レスポンス
 * @returns cytoscape ElementDefinition 配列
 * @graph-connects infra-graph-icons [calls] dominantKind → アイコン解決
 */
export function buildOverviewElements(overview: InfraGraphOverviewResponse): ElementDefinition[] {
  const maxCount = overview.stacks.reduce((max, stack) => Math.max(max, stack.count), 1);
  const nodes: ElementDefinition[] = overview.stacks.map((stack) => ({
    data: {
      id: `${STACK_NODE_PREFIX}${stack.id}`,
      stackId: stack.id,
      type: 'stack',
      label: stack.label,
      kind: stack.dominantKind,
      icon: iconForKind(stack.dominantKind),
      count: stack.count,
      size: overviewNodeSize(stack.count, maxCount),
    },
  }));
  // cross-stack エッジは nodes 集合と無関係に集約されるため、iam_member 除外後に
  // InfraResource ノードが残らない stack を端点に持つ dangling エッジが生じうる。
  // 端点ノードの無いエッジを cy.add() に渡すと cytoscape が throw し俯瞰全体が描画不能になるので、
  // 詳細ビュー（buildDetailElements）と同じく両端が在席するエッジだけ残す。
  const presentStacks = new Set(nodes.map((node) => node.data.id));
  const edges: ElementDefinition[] = overview.edges
    .filter(
      (edge) =>
        presentStacks.has(`${STACK_NODE_PREFIX}${edge.source}`) &&
        presentStacks.has(`${STACK_NODE_PREFIX}${edge.target}`),
    )
    .map((edge, index) => ({
      data: {
        id: `CE${String(index)}`,
        source: `${STACK_NODE_PREFIX}${edge.source}`,
        target: `${STACK_NODE_PREFIX}${edge.target}`,
        etype: edge.type,
        color: edgeColor(edge.type),
        width: aggregatedEdgeWidth(edge.count),
      },
    }));
  return [...nodes, ...edges];
}

/**
 * stack 詳細ビューの cytoscape 要素（リソースノード + stack 内エッジ）を組み立てる。
 * @param detail stack 詳細レスポンス
 * @returns cytoscape ElementDefinition 配列
 * @graph-connects infra-graph-icons [calls] kind → アイコン解決
 */
export function buildDetailElements(detail: InfraGraphStackDetailResponse): ElementDefinition[] {
  const present = new Set(detail.nodes.map((node) => node.id));
  const nodes: ElementDefinition[] = detail.nodes.map((node) => ({
    data: {
      id: node.id,
      type: 'res',
      label: shortLabel(node.name),
      kind: node.kind,
      icon: iconForKind(node.kind),
      full: node.name,
    },
  }));
  const edges: ElementDefinition[] = detail.edges
    .filter((edge) => present.has(edge.source) && present.has(edge.target))
    .map((edge, index) => ({
      data: {
        id: `IE${String(index)}`,
        source: edge.source,
        target: edge.target,
        etype: edge.type,
        color: edgeColor(edge.type),
        width: 1.6,
      },
    }));
  return [...nodes, ...edges];
}

/**
 * 与えた要素集合に含まれるエッジ種別の一覧（凡例トグル用、ソート済み）。
 * @param elements cytoscape 要素
 * @returns エッジ種別の昇順配列
 * @graph-connects none
 */
export function edgeTypesOf(elements: ElementDefinition[]): string[] {
  const types = new Set<string>();
  for (const element of elements) {
    const etype: unknown = element.data.etype;
    if (typeof etype === 'string') {
      types.add(etype);
    }
  }
  return [...types].sort();
}
